import { Component, ViewChild, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LeasingService } from '../../../core/services/leasing.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FindQuote } from '../../../core/models/leasing/FindQuote';
import { ResponseBase } from '../../../core/models/ResponseBase';
import { GetRoles } from '../../../core/models/GetRoles';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { ChargingComponent } from '../../../shared/components/charging/charging.component';
import { CommonModule } from '@angular/common';
import { BranchOfficeModel, BranchOffices } from '../../../core/models/leasing/BranchOffices';
import { DataService } from '../../../core/services/data.service';
import { filter, firstValueFrom, take } from 'rxjs';
import { UtilsService } from '../../../core/services/utils.service';
import { LinkPersonJsonData } from '../../../core/models/TenantLink';
import { BusinessLogicService } from '../../../core/services/business-logic.service';
import { TenantQuoteData } from '../../../core/models/leasing/TenantQuoteData';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-box-login',
  standalone: true,
  imports: [FormsModule, ModalComponent, ChargingComponent, CommonModule],
  templateUrl: './box-login.component.html',
  styleUrl: './box-login.component.scss'
})
export class BoxLoginComponent implements OnInit {
  // === Dependencias de componentes ===
  @ViewChild(ModalComponent) modal!: ModalComponent;
  @ViewChild(ChargingComponent) charging!: ChargingComponent;

  // === Variables de control ===
  processStep: number = 1;
  procesoContinua: boolean = false;
  isLinked: boolean = false;
  loginRole: number | null = null;

  // === Entrada del usuario ===
  quotationNumber: number | null = null;
  quoteRol: number | null = null;
  recoveryMethod: number | null = null;
  recoveryValue: string | null = null;
  securityCode: string[] = ['', '', '', ''];

  // === Informacion del usuario ===
  departamento: string = '';
  municipio: string = '';

  // === Respuestas ===
  quoteResponse: ResponseBase<FindQuote> | null = null;
  sucursales: BranchOfficeModel[] = [];

  // === Estado de los botones ===
  isFindQuotationButtonDisabled: boolean = true;
  isForgotQuotationButtonDisabled: boolean = true;

  get isCodeComplete(): boolean {
    return this.securityCode.every(code => code.length === 1);
  }

  LinkUrl: string = environment.LinkUrl;

  constructor(
    private leasingService: LeasingService,
    private dataService: DataService,
    private utilsService: UtilsService,
    private businessLogicService: BusinessLogicService
  ) { }

  ngOnInit() {
    // Primero esperamos que el DataService esté inicializado
    this.dataService.isInitialized$
      .pipe(
        filter(initialized => initialized), // Solo continúa cuando sea true
        take(1) // Toma solo la primera emisión y completa
      )
      .subscribe(() => {
        this.loginRole = this.dataService.getLoginRole();
        const quotationNumber = this.dataService.getQuotationNumber();

        if (this.loginRole !== null && quotationNumber !== null) {
          this.processStep = 2;
          this.quotationNumber = quotationNumber;
          this.isLinked = true;
        }
      });

    //Resto del código del ngOnInit...
  }

  onFindQuotationInputChange() {
    this.isFindQuotationButtonDisabled = this.quotationNumber === null || this.quotationNumber <= 0;
  }

  onFindQuotationHeaderByOwner() {
    this.isForgotQuotationButtonDisabled = this.recoveryValue === null || this.recoveryValue.trim() === '';
  }

  onFindQuote() {
    this.charging.showSpinner();
    if (this.quotationNumber) {
      this.leasingService.FindQuote(this.quotationNumber).subscribe({
        next: (response: ResponseBase<FindQuote>) => {
          if (response.value) {
            this.quoteResponse = response;
            this.dataService.updateFindQuoteData(response.value);
            this.leasingService.GetRoles().subscribe({
              next: (response: ResponseBase<GetRoles>) => {
                if (response.value) {
                  this.quotationNumber = this.quoteResponse?.value.inIdQuoteHeader!;
                  this.quoteRol = this.quoteResponse?.value.inIdRole!;
                  this.leasingService.FindBranchOfficesByQuoteHeader(this.quotationNumber!).subscribe({
                    next: (response: ResponseBase<BranchOffices>) => {
                      if (response.value) {
                        if (response.value.blProcesoContinua) {
                          this.procesoContinua = response.value.blProcesoContinua;
                          this.sucursales = response.value.sucursal;
                          this.departamento = response.value.strDepto;
                          this.municipio = response.value.strMunicipio;
                        }
                        this.processStep = 2;
                        this.charging.hideSpinner();
                      } else {
                        this.charging.hideSpinner();
                        this.modal.openModal('Ha ocurrido un error inesperado', '', false);
                      }
                    },
                    error: (error: HttpErrorResponse) => {
                      this.charging.hideSpinner();
                      console.error('Error GetRoles:', error);
                    }
                  });
                } else {
                  this.modal.openModal('Ha ocurrido un error inesperado', '', false);
                }
              },
              error: (error: HttpErrorResponse) => {
                console.error('Error GetRoles:', error);
              }
            });
          } else {
            this.charging.hideSpinner();
            this.modal.openModal('Ha ocurrido un error', 'No se encontraron datos relacionados con la cotización.', false);
          }
        },
        error: (error: HttpErrorResponse) => {
          this.charging.hideSpinner();
          console.error('Error FindQuote:', error);
        }
      });
    }
  }

  onSendEmail() {
    if (this.quotationNumber) {
      this.charging.showSpinner();
      const userEmail = this.dataService.getQuoteData()?.strContactEmail;

      this.leasingService.SendEmailCodeAuthotization(this.quotationNumber!)
        .subscribe({
          next: () => {
            this.charging.hideSpinner();
            const message = userEmail
              ? `Se ha enviado el código de seguridad al correo electrónico ${this.utilsService.obfuscateEmail(userEmail)}`
              : 'Se ha enviado el código de seguridad';
            this.modal.openModal('Envio de codigo de seguridad', message, true);
            this.processStep = 3;
          },
          error: (error: HttpErrorResponse) => {
            this.charging.hideSpinner();
            this.modal.openModal('Envio de codigo de seguridad', 'Ha ocurrido un error con el envio del código de seguridad', false);
            console.error('Error SendEmailCodeAuthotization:', error);
          }
        });
    }
  }

  onSecurityCodeInput(event: Event, inputIndex: number): void {
    const input = event.target as HTMLInputElement;

    if (!/^\d$/.test(input.value) && input.value !== '') {
      input.value = '';
      return;
    }

    this.securityCode[inputIndex - 1] = input.value;

    if (input.value.length === 1 && inputIndex < 4) {
      const nextInput = document.querySelector(`.code-box:nth-child(${inputIndex + 1})`) as HTMLInputElement;
      if (nextInput) nextInput.focus();
    }
  }

  onSecurityCodeKeyDown(event: KeyboardEvent, inputIndex: number): void {
    const input = event.target as HTMLInputElement;

    if (event.key === 'Backspace' && input.value === '') {
      if (inputIndex > 1) {
        const previousInput = document.querySelector(
          `.code-box:nth-child(${inputIndex - 1})`
        ) as HTMLInputElement;
        if (previousInput) previousInput.focus();
      }
    }
  }

  verifyCode(): void {
    if (this.isCodeComplete) {
      this.charging.showSpinner();
      const securityCodeString: string = this.securityCode.join('');
      const securityCodeNumber: number = parseInt(securityCodeString, 10);

      this.leasingService.ValidateCodeAuthotization(this.quotationNumber!, securityCodeNumber)
        .subscribe({
          next: async (response: any) => {
            if (response.value.inSNAuthorizationCode == -1) {
              try {
                const quoteData = this.dataService.getQuoteData();
                await this.businessLogicService.loadAndProcessLinkQuoteData(this.quotationNumber!);
                const tenantData = this.dataService.getTenantQuoteData() as TenantQuoteData;             

                if (quoteData?.inIdCurrentProcessStatus == 10 || (tenantData && Object.keys(tenantData).length > 0 && quoteData?.inIdCurrentProcessStatus == 8)) {
                  const radicationMainRole = await this.ConsultAndSetRadicationMainRole();

                  if (radicationMainRole != null) {
                    this.loginRole = radicationMainRole;
                    await this.dataService.setLoginRole(this.loginRole);
                    this.processStep = 8;
                  }
                } else {
                  this.processStep = 4;
                }
              } catch (error) {
                console.error('Error processing link data:', error);
                this.modal.openModal(
                  'Error',
                  'Ocurrió un error al procesar la información de vinculación',
                  false
                );
              }
            } else {
              this.modal.openModal(
                'Ha ocurrido un error con el código de seguridad',
                'Código de seguridad inválido',
                false
              );
            }
            this.charging.hideSpinner();
          },
          error: (error: HttpErrorResponse) => {
            this.charging.hideSpinner();
            this.modal.openModal(
              'Ha ocurrido un error con el código de seguridad',
              'Código de seguridad inválido',
              false
            );
            console.error('Error ValidateCodeAuthotization:', error);
          }
        });
    }
  }
  

  onUserRolChange(value: number) {
    this.loginRole = value;
  }

  onRecoveryMethodSelected() {
    this.processStep = 7;
  }

  onFindQuoteByOwner() {
    if (this.recoveryValue) {
      this.charging.showSpinner();
      let email = this.recoveryMethod === 1 ? this.recoveryValue : undefined;
      let telephone = this.recoveryMethod === 2 ? this.recoveryValue : undefined;

      this.leasingService.FindQuotesHeaderByOwner(email, telephone)
        .subscribe({
          next: (response: ResponseBase<any>) => {
            this.charging.hideSpinner();
            if (!response.status) {
              this.modal.openModal('Ha ocurrido un error', 'No hay datos de cotización asociadas a la busqueda', false);
            } else {
              this.modal.openModal('', '', true);
            }
          },
          error: (error: HttpErrorResponse) => {
            this.charging.hideSpinner();
            this.modal.openModal('Ha ocurrido un error', 'No hay datos de cotización asociadas a la busqueda', false);
            console.error('Error onFindQuoteByOwner:', error);
          }
        });
    }
  }

  getRoleText(): string {
    switch (this.loginRole) {
      case 1:
        return 'Arrendatario';
      case 2:
        return 'Propietario';
      case 3:
        return 'Codeudor 1';
      case 4:
        return 'Codeudor 2';
      default:
        return '';
    }
  }

  async onContinueToHome() {
    if (this.loginRole !== null && (this.processStep === 4 || this.processStep === 8)) {
      
      if(this.processStep === 4){
        const linkJson = {
          f0: {
            rolprincipal: true
          },
          f1: {}
        };

        const linkRequest = {
          inIdRole: this.loginRole,
          inIdQuoteHeader: this.quotationNumber!,
          strJson: JSON.stringify(linkJson),
          blUpdateLinkPerson: false
        };   

        await firstValueFrom(this.leasingService.AddOrUpdateLinkQuotePerson(linkRequest));
      }

      this.dataService.setRadicationMainRole(this.loginRole);
      await this.dataService.setLoginRole(this.loginRole!);
      await this.dataService.setLinked(0);
    }
    else if (this.loginRole !== null && this.isLinked) {
      this.ConsultAndSetRadicationMainRole();  
      await this.dataService.setLinked(1);
    }
    
    await this.dataService.setQuotationNumber(this.quotationNumber!);
    await this.dataService.changeView('home');
  }

  async ConsultAndSetRadicationMainRole(): Promise<number | null> {
    var tenantData = this.dataService.getTenantQuoteData();
  
    // Verifica si tenantData es null, undefined o un objeto vacío
    if (!tenantData || Object.keys(tenantData).length === 0) {
      // Llamar al servicio para cargar y procesar los datos
      await this.businessLogicService.loadAndProcessLinkQuoteData(this.quotationNumber!);
      tenantData = this.dataService.getTenantQuoteData();
    }
  
    for (const [roleId, data] of Object.entries(tenantData!)) {
      if (data.rolprincipal) {
        await this.dataService.setRadicationMainRole(parseInt(roleId));
        return parseInt(roleId);
      }
    }
  
    return null;
  }  
} 
